<template>
  <div>
    <v-container fluid grid-list-sm class="px-0 pb-2 pt-0">
      <StabilityTypeBar></StabilityTypeBar>
      <v-divider class="py-3"></v-divider>
      <StabilityLevelBar></StabilityLevelBar>
      <StabilityOpacityBar class="mt-4"></StabilityOpacityBar>
    </v-container>
  </div>
</template>

<script>
import dayjs from "dayjs";
import lodashGet from "lodash/get";
import { mapGetters } from "vuex";

import {
  GET_DETAILS_SIDEBAR,
  GET_STABILITY_REFRESH_MAP,
  SAVE_PREFERENCES,
  GET_STABILITY_PARAMS,
} from "@/store/actions.type";
import {
  MODULE_PREFIX_GRIDS,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_USER,
} from "@/store";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import {
  getTodayStartEndDate,
  nowUtc,
  getFormatted,
} from "@/common/date.utilities";
import { MAIN_MAP_ID, DEFAULT_OPACITY_PULSE } from "@/common/constants/maps";
import {
  addToMap as addStabilityToMap,
  removeFromMap as removeStabilityFromMap,
  buildPreferences,
  populateDefaultPreferences,
} from "@/services/grids/stability.service";
import {
  GRIDS_STABILITY_TYPES,
  GRIDS_STABILITY_LEVELS,
} from "@/common/constants/grids";
import { setGridLevelFilters } from "@/services/grids/grids.service";
import { isAdmin } from "@/services/user/user.service";
import { EVENT_ACTIONS, EVENT_CATEGORIES } from "@/common/constants/analytics";

import { hasEssentialsAccess } from "@/services/user/user.service";

export default {
  name: "StabilitySelector",
  props: {
    enabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    StabilityLevelBar: () =>
      import("@/components/grids/stability/StabilityLevelBar"),
    StabilityTypeBar: () =>
      import("@/components/grids/stability/StabilityTypeBar"),
    StabilityOpacityBar: () =>
      import("@/components/grids/stability/StabilityOpacityBar"),
  },
  data: () => ({
    endDate: null,
    forceClose: null,
    hasEssentials: hasEssentialsAccess(),
  }),
  mounted() {
    this.initialize();
  },
  computed: {
    ...mapGetters({
      stability: MODULE_PREFIX_GRIDS + "stability",
      maps: MODULE_PREFIX_MAP + "maps",
      mainMapHijacker: MODULE_PREFIX_MAP + "mainMapHijacker",
      preferences: MODULE_PREFIX_USER + "preferences",
      selectedDates: MODULE_PREFIX_HOME + "selectedDates",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
    }),
    map() {
      return this.maps[MAIN_MAP_ID];
    },
    layerId() {
      return `layer-${this._uid}`;
    },
    scoreField() {
      return "co";
    },
    filters() {
      if (
        this.stability.params &&
        this.stability.params.type &&
        this.stability.params.levels
      ) {
        let params = {};
        params.type = this.stability.params.type
          ? this.stability.params.type
          : null;
        params.levels = this.stability.params.levels
          ? this.stability.params.levels
          : null;
        return setGridLevelFilters(params);
      }
      return null;
    },
    opacity() {
      if (lodashGet(this.preferences, "grids.stability.opacity")) {
        return this.preferences.grids.stability.opacity / 100;
      }
      return DEFAULT_OPACITY_PULSE;
    },
  },
  methods: {
    handleClickClose() {
      this.forceClose = new Date().toISOString();
    },
    addToMap() {
      this.endDate = getTodayStartEndDate(this.selectedDates.end).start; // needs to be like this 2019-02-07T00:00:00.000Z
      const PARAMS = {
        id: this._uid,
        type: this.stability.params.type,
        layerId: this.layerId,
        mapClickEvent: this.mapClickEvent,
        map: this.map,
        filters: this.filters,
        end: this.endDate,
        opacity: this.opacity,
      };

      if (PARAMS.filters) {
        addStabilityToMap(PARAMS);
      } else {
        console.warn(
          "StabilitySelector.vue addToMap function: filters not set so not drawing pulse tiles"
        );
      }
    },
    removeFromMap() {
      const PARAMS = {
        map: this.map,
        layerId: this.layerId,
      };
      removeStabilityFromMap(PARAMS);
    },
    mapClickEvent(e, features) {
      e.originalEvent.stopPropagation();
      if (features.length > 0) {
        const FEATURE = features[0];
        let visible = false;
        const SCORE = FEATURE.properties[this.scoreField];
        if (this.filters && Array.isArray(this.filters)) {
          for (let i = 0; i < this.filters.length; i++) {
            const FILTER = this.filters[i];
            const MIN = FILTER.min ? FILTER.min : -100000;
            const MAX = FILTER.max ? FILTER.max : 100000;
            if (SCORE >= MIN && SCORE < MAX) {
              visible = FILTER.selected;
              break;
            }
          }
        }

        if (visible) {
          features[0].properties.stabilityType = this.stability.params.type;
          this.detailsSidebar.features = features;
          this.detailsSidebar.visible = true;
          this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.monitor;
          this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.monitor;
          this.detailsSidebar.monitorType = DETAIL_SIDEBAR_TYPES.stability;
          this.$store.dispatch(
            MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
            this.detailsSidebar
          );
        }

        // check if tiles are stale, if so write a console log for admins
        if (isAdmin) {
          if (lodashGet(features[0], "properties.lu")) {
            const tileDate = dayjs(features[0].properties.lu);
            const diffHours = dayjs(nowUtc()).diff(tileDate, "hours");
            if (diffHours > 4) {
              const formattedDate = getFormatted(tileDate);
              console.error(
                `Pulse map tiles are stale (${diffHours} hours old).\n Current tiles from ${formattedDate}.`
              );
              console.error(
                "Selected tiles properties:\n",
                JSON.stringify(features[0].properties, null, 2)
              );
            }
          }
        }
        if (FEATURE.properties.stabilityType) {
          let eventCategory, eventName;
          if (FEATURE.properties.stabilityType === "country") {
            eventCategory = EVENT_CATEGORIES.pulse_country;
            eventName = FEATURE.properties.dn;
          }
          if (FEATURE.properties.stabilityType === "region") {
            eventCategory = EVENT_CATEGORIES.pulse_region;
            eventName = FEATURE.properties.g;
          }
          if (FEATURE.properties.stabilityType === "city") {
            eventCategory = EVENT_CATEGORIES.pulse_city;
            eventName = FEATURE.properties.ci;
          }
          if (FEATURE.properties.stabilityType === "county") {
            eventCategory = EVENT_CATEGORIES.pulse_county;
            eventName = FEATURE.properties.cnty;
          }
          this.handlePageTrackerEvent(
            eventCategory,
            EVENT_ACTIONS.clicked,
            eventName
          );
        }
      }
    },
    initialize(params = {}) {
      //TODO populateDefaultPreferences and buildPreferences seem redundant. what can be done?
      let prefs = populateDefaultPreferences(this.preferences);

      //handle situations where a user wouldn't see any pulse content due to previously turning it off
      if (params.toggle && this.enabled) {
        let changeHappened = false;
        params = this.stability.params;
        if (
          !prefs.grids.stability.opacity ||
          prefs.grids.stability.opacity === 0
        ) {
          prefs.grids.stability.opacity = 50;
          params.opacity = 50;
          changeHappened = true;
        }
        if (
          !prefs.grids.stability.levels ||
          prefs.grids.stability.levels.length === 0
        ) {
          let levels = GRIDS_STABILITY_LEVELS.map((stabilityLevel) => {
            return stabilityLevel.id;
          });
          prefs.grids.stability.levels = levels;
          params.levels = levels;
          changeHappened = true;
        }
        if (!prefs.grids.stability.type) {
          prefs.grids.stability.type = GRIDS_STABILITY_TYPES.country;
          params.type = GRIDS_STABILITY_TYPES.country;
          changeHappened = true;
        }
        if (changeHappened) {
          this.$store.dispatch(
            MODULE_PREFIX_GRIDS + GET_STABILITY_PARAMS,
            params
          );
        }
      }
      const PREF_PARAMS = buildPreferences({
        preferences: prefs,
        enabled: this.enabled,
      });
      this.$store.dispatch(MODULE_PREFIX_USER + SAVE_PREFERENCES, PREF_PARAMS);
      if (!this.enabled) {
        this.removeFromMap();
      } else {
        //TODO need to deal with situation where no type is selected...
        this.$store.dispatch(
          MODULE_PREFIX_GRIDS + GET_STABILITY_REFRESH_MAP,
          true
        );
      }
    },
    handleMapRefresh(force) {
      if (
        this.map &&
        (this.stability.refreshMap || force) &&
        !this.hasEssentials
      ) {
        if (
          lodashGet(this.stability, "params.type") &&
          (lodashGet(this.preferences, "grids.stability.enabled") ||
            lodashGet(this.preferences, "grids.stability.enabled") !== false) &&
          !this.mainMapHijacker
        ) {
          this.addToMap();
        } else {
          this.removeFromMap();
        }
      }
      this.$store.dispatch(
        MODULE_PREFIX_GRIDS + GET_STABILITY_REFRESH_MAP,
        false
      );
    },
  },
  watch: {
    mainMapHijacker: {
      handler(newVal, oldVal) {
        this.handleMapRefresh(newVal !== oldVal);
      },
    },
    "stability.refreshMap": {
      handler() {
        this.handleMapRefresh();
      },
    },
    "stability.params": {
      handler() {
        this.initialize({ toggle: true });
      },
    },
    enabled: {
      handler() {
        this.initialize({ toggle: true });
      },
    },
  },
};
</script>

<style scoped></style>
